import type { InterfaceLinkInstanceId } from '@/types/ids';
import { CircuitType } from '@/schemas/common';
import { InterfaceType } from '@/models/ChipModel';
import type { IChipEditorModel } from '@/interfaces/models';
import type { TileInstance } from './TileInstance';
import { TileType } from '@/models/TileTypes';
import {
  BundleInterfaceStateSchema,
  type BundleInterfaceState,
  Side,
  TileSideIOType,
} from '@/schemas/tiles';
import type { TileDefinition } from './TileDefinition';
import { drawBundleInterface } from '@/utils/drawBundleInterface';

/** A physical, rotatable boundary between a whole bundle and one scalar net. */
export class BundleInterfaceTile implements TileDefinition<BundleInterfaceState> {
  type = TileType.BundleInterface;
  name = 'Bundle interface';
  canRotate = true;
  stateSchema = BundleInterfaceStateSchema;
  createDefaultState = () => ({});
  eventHandlers = {
    placed: ({
      instance,
      chipEditorModel,
    }: {
      instance: TileInstance<BundleInterfaceState>;
      chipEditorModel: IChipEditorModel;
    }) => {
      const chip = chipEditorModel.chipModel;
      if (
        chip.kind !== 'bundle-adapter' ||
        chipEditorModel.editingMode !== CircuitType.ChipStructure
      )
        return;
      const old = instance.state.connectedInterface?.interfaceLinkId;
      const link =
        (old && chip.interfaceLinkMap.get(old as InterfaceLinkInstanceId)) ||
        chip.createInterfaceLink(
          InterfaceType.BUNDLE,
          `Bundle ${chip.interfaceLinks.filter((link) => link.type === InterfaceType.BUNDLE).length + 1}`,
        );
      instance.state.connectedInterface = {
        chipInstanceId: chip.editingInstance.id,
        circuitType: CircuitType.ChipStructure,
        interfaceLinkId: link.id,
      };
      chip.addInterfaceTile(link.id, instance, CircuitType.ChipStructure);
      instance.adapterPort = true;
      instance.effectiveIoSides = {
        ...this.ioSides,
        [Side.RIGHT]: { type: TileSideIOType.NONE, forceConnection: false },
      };
      chip.rebuildTemplateGraph();
    },
  };
  ioSides = {
    [Side.LEFT]: { type: TileSideIOType.INPUT_OUTPUT, forceConnection: true },
    [Side.RIGHT]: { type: TileSideIOType.INPUT_OUTPUT, forceConnection: true },
    [Side.TOP]: { type: TileSideIOType.NONE, forceConnection: false },
    [Side.BOTTOM]: { type: TileSideIOType.NONE, forceConnection: false },
  };
  graphics = { type: TileType.BundleInterface, draw: drawBundleInterface };
}
